// react
import { ChangeEvent, useMemo } from 'react';

// types
import { EntityPayload, EntityType, Feedback, UserPayload } from '../../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// mui
import { Alert, AlertTitle, Box, Checkbox, Chip, Grid, InputLabel, SxProps } from '@mui/material';

// tables
import { TableBasicUsersOnboard } from '../../../tables/basic/TableBasicUsersOnboard/TableBasicUsersOnboard';

// fields
import { Select } from '../../../fields/Select/Select';

// ui
import { BtnExpand } from '../../../ui/BtnExpand/BtnExpand';

// options
import { getOptionsEntityTypes } from '../../../../utils/options';

// props
export interface EntityOnboardProps {
	defaultValue?: EntityType;
	disabled?: boolean;
	entity: EntityPayload;
	feedback?: Feedback | null;
	isChecked?: boolean;
	isExpanded?: boolean;
	isNew?: boolean;
	onChangeAllUsers?: (entity: EntityPayload, checked: boolean) => void;
	onChangeEntity?: (
		e: ChangeEvent<HTMLInputElement>,
		checked: boolean,
		entity: EntityPayload
	) => void;
	onChangeUser?: (
		e: ChangeEvent<HTMLInputElement>,
		checked: boolean,
		user: UserPayload,
		entity: EntityPayload
	) => void;
	onClickExpand?: (entityId: string) => void;
	selectedUserIds?: Set<string>;
}

export const EntityOnboard = ({
	defaultValue,
	disabled,
	entity,
	feedback,
	isChecked,
	isExpanded = false,
	isNew,
	onChangeAllUsers,
	onChangeEntity,
	onChangeUser,
	onClickExpand,
	selectedUserIds,
}: EntityOnboardProps) => {
	// hooks
	const { t } = useTranslation();

	// vars
	const entityUsers = useMemo(() => entity.users ?? [], [entity]);
	const id = `entity-${entity.idWbg}`;

	const optionsEntityTypes = getOptionsEntityTypes(t);

	return (
		<Box>
			<Box
				sx={(theme) => {
					const sx: SxProps = {
						padding: theme.spacing(2),
						minWidth: '100%',
					};

					return sx;
				}}>
				<Grid alignItems="center" container spacing={2}>
					<Grid item xs={6}>
						<Grid alignItems="center" container spacing={1}>
							<Grid item xs="auto">
								<Checkbox
									checked={disabled ? false : isChecked}
									disabled={disabled}
									disableRipple={true}
									id={id}
									name={id}
									onChange={(e, isChecked) => {
										if (onChangeEntity) {
											onChangeEntity(e, isChecked, entity);
										}
									}}
									sx={(theme) => {
										return {
											padding: 0,
										};
									}}
									value={entity.idWbg}
								/>
							</Grid>
							<Grid item xs={true}>
								<InputLabel
									disabled={disabled}
									htmlFor={disabled ? '' : id}
									sx={(theme) => {
										return {
											margin: 0,
											whiteSpace: 'normal',
										};
									}}>
									{entity.name}
								</InputLabel>
							</Grid>
							{isNew !== undefined && (
								<Grid item xs={3}>
									{isNew && (
										<Chip label={t('new')} size="small" color="success" variant="twoTone" />
									)}
								</Grid>
							)}
						</Grid>
					</Grid>
					<Grid item xs={6} md={5}>
						<Select
							aria-label={t('type')}
							defaultValue={defaultValue || 'PIU'}
							disabled={disabled}
							hasNoneOption={false}
							id={`${id}-type`}
							name={`${id}-type`}
							options={optionsEntityTypes}
							required={true}
						/>
					</Grid>
					<Grid item xs={12} md={1}>
						<BtnExpand
							isExpanded={isExpanded}
							label={isExpanded ? t('hideUsers') : t('seeUsers')}
							onClick={() => {
								if (onClickExpand && entity.idWbg) {
									onClickExpand(entity.idWbg);
								}
							}}
						/>
					</Grid>
					{feedback && (
						<Grid item xs={12}>
							<Alert severity="error" {...feedback.alert}>
								{feedback.title && <AlertTitle {...feedback.title} />}
								{feedback.alert.children}
							</Alert>
						</Grid>
					)}
				</Grid>
			</Box>
			{isExpanded && (
				<TableBasicUsersOnboard
					color="grey"
					data={entityUsers}
					entity={entity}
					isChecked={isChecked}
					isDisabled={disabled}
					onChangeAllUsers={onChangeAllUsers}
					onChangeUser={onChangeUser}
					selectedUserIds={selectedUserIds}
				/>
			)}
		</Box>
	);
};
